
import {
  Dialog,
  Flex,
  ProviderConsumer as FluentUIThemeConsumer
} from '@fluentui/react-northstar';
import * as AdaptiveCards from 'adaptivecards';
import * as ACDesigner from 'adaptivecards-designer';
import * as markdownit from 'markdown-it';
import * as monaco from 'monaco-editor';
import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

// if you want to bundle the designer CSS using something like mini-css-loader:
import "adaptivecards-designer/dist/adaptivecards-designer.css";

// Uncomment below if you choose to pass an empty hostContainers array
import { mergeStyleSets, Panel, PanelType } from '@fluentui/react';
import { themeNames } from '@fluentui/react-teams';
import { CardDesigner, IVersionedChoice } from 'adaptivecards-designer';
import 'adaptivecards-designer/dist/adaptivecards-defaulthost.css';
import { useTranslation } from 'react-i18next';
import AppCSS from '../App.module.css';
import { appState, useSetState } from '../AppState';
import { CustomPropertyDataType_LABELS } from '../custom-properties/utils/constants';
import { ThemeColorScheme } from '../shared/common/TeamsTheme';
import { TicketTemplate } from '../shared/interfaces/ticket.interface';
import { AdaptiveCardTexts, TEMPLATE, TicketPropertiesJsonTypeNames } from '../shared/utils/constants';
import { AddComment } from './CustomComponents/AddComment';
import { Assignment } from './CustomComponents/Assignment';
import { BoolProperty } from './CustomComponents/CustomProperties/BoolProperty';
import { DateProperty } from './CustomComponents/CustomProperties/DateProperty';
import { ListProperty } from './CustomComponents/CustomProperties/ListProperty';
import { NumProperty } from './CustomComponents/CustomProperties/NumProperty';
import { TextProperty } from './CustomComponents/CustomProperties/TextProperty';
import { TimeProperty } from './CustomComponents/CustomProperties/TimeProperty';
import { CustomElementDataSourceMain } from './CustomComponents/DataSource/DataSourceMain';
import { IDataSource } from './CustomComponents/DataSource/DataSourceTable';
import { CardPeoplePicker } from './CustomComponents/PeoplePicker/CardPeoplePicker';
import { GroupPicker } from './CustomComponents/Pickers/GroupPicker';
import { TeamPicker } from './CustomComponents/Pickers/TeamPicker';
import { RequestTitle } from './CustomComponents/RequestTitle';
import AC_Custom_Styles from './CustomComponents/Style/adaptivecard_overrides.css';
import { SubmitRequest } from './CustomComponents/SubmitRequest';
import { initializeCustomFieldConfigurations, initializeCustomPropertiesState, initializeDataSource, initialPickerChoices } from './CustomComponents/Utils/CustomElementUtils';
import { loadFeatureVars, registerActionsDesignerCustomComponents, registerCustomPropertiesDesignerCustomComponents, registerDesignerCustomComponents, registerExtrasDesignerCustomComponents } from './CustomComponents/Utils/CustomRegistration';
import { SetDefault_CustomForm } from './SetDefault_CustomForm';
import { ITemplateFormProperties } from './TicketTemplates';

export const blankPayloadData = {
  $schema: "http://adaptivecards.io/schemas/adaptive-card.json",
  type: "AdaptiveCard",
  version: "1.2",
  body: [],
};

export const AdaptiveCardDesigner = (props: ITemplateFormProperties) => {

  const { templateData, dispatchData, headerContent, reRender } = props;
  const { t } = useTranslation();
  const currentState = appState();
  const setAppState = useSetState();
  const ref = useRef<HTMLDivElement>(null);
  const [tempData, setTempData] = useState<TicketTemplate>(
    Object.assign({}, templateData)
  );
  const [open, setOpen] = useState<boolean>(false);
  const [openDataSource, setOpenDataSource] = useState<boolean>(false);
  const [cardDesigner, setCardDesigner] = useState<CardDesigner>(undefined);
  const [isMDPEnabled, setIsMDPEnabled] = useState<boolean>(false);
  const [selectedPeer, setSelectedPeer] = useState<any>();
  const [cpDataSources, setCpDataSources] = useState<IDataSource[]>();

  const isDesigningInLightMode = () => {
    let label = document.querySelector(".ms-dropdown-label");
    if (label?.innerHTML === "Microsoft Teams - Light") {
      return true;
    }
    return false;
  };

  const classNames = mergeStyleSets({
    acdDesignerRoot: {
      overflow: "auto !important",
      maxHeight: "88%",
      display: "block",
      ".content": {
        maxHeight: "88%",
        minHeight: "88%",
        border: "thin solid lightgrey",
      },

      ".acd-designer-host, .cardHost": {
        marginTop: "8px !important",
      },

      ".acd-toolbar-button": {
        ":hover, :active, :focus": {
          background: "rgb(61,61,61) !important",
        },
      },

      ".default-ac-textBlock, .acd-toolbar-label": {
        color: "white !important ",
      },

      ".acd-propertySheet-pane, .acd-designer-cardArea, .acd-previewRightAndBottomDocks, .acd-treeView-pane, .acd-toolPalette-pane, .monaco-editor-background, .margin-view-overlays, .acd-treeView-host":
      {
        color: "white",
        background: "rgb(41,41,41) !important",
      },

      ".acd-palette-category, .default-ac-container, .acd-toolbar": {
        background: "rgb(51,51,51)",
        color: "white",
      },

      ".default-ac-container div": {
        background: "rgb(51,51,51)",
        color: "white",
      },

      ".acd-palette-item": {
        ":hover, :active, :focus": {
          background: "rgb(51,51,51) !important",
        },
      },

      ".acd-tree-item": {
        color: "white",
        background: "rgb(51,51,51)",
        ":hover, :active, :focus": {
          background: "rgb(61,61,61) !important",
        },
      },

      ".acd-toolbox-header-title": {
        color: "white",
      },

      ".ms-ctrl": {
        background: "rgb(71,71,71) !important",
      },
      ".ms-dropdown-label, .ac-textBlock, .ac-textRun, .mtk1": {
        color: "white",
      },

      ".ms-ctrl-dropdown": {
        background: "rgb(71,71,71) !important",
        color: "white !important",
        ":hover, :active, :focus": {
          background: "rgb(61,61,61) !important",
        },
      },
      ".default-ac-multichoiceInput, .default-ac-textInput, .default-ac-numberInput":
      {
        background: "rgb(91,91,91) !important",
        color: "white",
      },

      ".default-ac-container.default-ac-adaptiveCard": {
        background: "rgb(51,51,51) !important",
      },

      '.default-ac-pushButton, .default-ac-selectable': {
        background: 'rgb(51,51,51) !important'
      },
      '.tikit-button': {
        backgroundColor: 'unset !important',
        borderColor: 'lightgrey !important',
        color: 'black !important'
      },
      '.tikit-cardPicker': {
        '--input-background-color': 'transparent !important'
      },
    },
    AC_Custom_Styles
  });
  const classNamesLightMode = mergeStyleSets({
    acdDesignerRoot: {
      overflow: "auto !important",
      maxHeight: "100%",
      display: "block",
      ".content": {
        maxHeight: "78%",
        minHeight: "78%",
        border: "thin solid lightgrey",
      },

      ".acd-designer-host, .cardHost": {
        marginTop: "8px !important",
      },
      ".default-ac-pushButton, .default-ac-selectable": {
        background: "",
      },
    },
    AC_Custom_Styles
  });

  const setDesigner = (element, featureSets, currState) => {
    ACDesigner.GlobalSettings.enableDataBindingSupport = true;

    ACDesigner.CardDesigner.onProcessMarkdown = (text, result) => {
      result.outputHtml = new markdownit().render(text);
      result.didProcess = true;
    };

    AdaptiveCards.GlobalRegistry.elements.unregister("Media");
    ACDesigner.CardDesignerSurface.cardElementPeerRegistry.unregisterPeer(
      AdaptiveCards.Media
    );

    let hostContainers: ACDesigner.HostContainer[] = [
      new ACDesigner.TeamsContainer
    ];

    registerDesignerCustomComponents(hostContainers, featureSets, templateData);
    if (featureSets.enableCustomProperties)
      registerCustomPropertiesDesignerCustomComponents(hostContainers, featureSets, currState);

    if (templateData.IsDefault)
      registerActionsDesignerCustomComponents(hostContainers);

    registerExtrasDesignerCustomComponents(hostContainers);

    const customDesignerPeer = ACDesigner.DesignerPeer;
    customDesignerPeer.prototype.onClick = (sender) => {
      setSelectedPeer(sender);
    }

    const customElementPaletteItem = ACDesigner.ElementPaletteItem;

    let newPeerCreated = false;
    customElementPaletteItem.prototype.createPeer = function (context, designerSurface) {
      newPeerCreated = true;

      let el: AdaptiveCards.CardElement;
      const objType = new this.typeRegistration.objectType();
      if (this.typeRegistration.typeName != objType.getJsonTypeName()) {
        const customProperties = currentState?.customProperties ?? [];
        const cp = customProperties.find(s => s.Name == this.typeRegistration.typeName);
        switch (CustomPropertyDataType_LABELS[cp.DataType]) {
          case CustomPropertyDataType_LABELS.STRING:
            el = new TextProperty(this.typeRegistration.typeName, cp);
            break;
          case CustomPropertyDataType_LABELS.INT:
            el = new NumProperty(this.typeRegistration.typeName, cp);
            break;
          case CustomPropertyDataType_LABELS.BOOL:
            el = new BoolProperty(this.typeRegistration.typeName, cp);
            break;
          case CustomPropertyDataType_LABELS.DATE:
            el = new DateProperty(this.typeRegistration.typeName, cp);
            break;
          case CustomPropertyDataType_LABELS.TIME:
            el = new TimeProperty(this.typeRegistration.typeName, cp);
            break;
          case CustomPropertyDataType_LABELS.LIST:
            el = new ListProperty(this.typeRegistration.typeName, cp);
            break;
          default:
            break;
        }

      } else
        el = new this.typeRegistration.objectType();
      return ACDesigner.CardDesignerSurface.cardElementPeerRegistry.createPeerInstance(designerSurface, null, el, true);
    };

    let designer = new ACDesigner.CardDesigner(hostContainers);
    /* Modify the toolbar */
    let myButton = new ACDesigner.ToolbarButton(
      "myButton",
      TEMPLATE.DESIGNER_CUSTOM_BUTTON,
      "acd-icon-newCard",
      (sender) => {
        setOpen(true);
      }
    );
    designer.toolbar.insertElementBefore(
      myButton,
      ACDesigner.CardDesigner.ToolbarCommands.HostAppPicker
    );

    let myButton2 = new ACDesigner.ToolbarButton(
      'myButton2',
      "Data Sources",
      'acd-icon-table',
      sender => {
        setOpenDataSource(true);
      }
    );

    designer.toolbar.insertElementAfter(
      myButton2,
      ACDesigner.CardDesigner.ToolbarCommands.TogglePreview
    );

    const previewEl = designer.toolbar.getElementById(ACDesigner.CardDesigner.ToolbarCommands.TogglePreview);
    if (previewEl)
      previewEl.isVisible = false;
    designer.bindingPreviewMode = ACDesigner.BindingPreviewMode.SampleData;

    designer.attachTo(element);
    designer.monacoModuleLoaded(monaco);

    designer.toolbar["_elements"].map((x) => {
      const hiddenToolbars = [
        ACDesigner.CardDesigner.ToolbarCommands.NewCard,
        ACDesigner.CardDesigner.ToolbarCommands.CopyJSON,
        ACDesigner.CardDesigner.ToolbarCommands.Help,
      ];
      if (hiddenToolbars.includes(x.id)) x["_renderedElement"]["hidden"] = true;
      return x;
    });

    designer.targetVersion = AdaptiveCards.Versions.v1_4;
    designer.jsonEditorToolbox.collapse();
    designer.treeViewToolbox.collapse();

    if (
      templateData.CardResponseJson &&
      templateData.CardResponseJson.length > 0
    ) {
      let jsonObject = JSON.parse(templateData.CardResponseJson);
      parseCustomPropertyInputs(jsonObject["body"]);
      designer.setCard(jsonObject);
    }
    else designer.setCard(blankPayloadData);

    let previousCardElementsCount = 0;

    designer.onCardValidated = (
      cd: ACDesigner.CardDesigner,
      _validationLogEntries: AdaptiveCards.IValidationEvent[]
    ) => {
      const currentCard = cd.getBoundCard();
      const currentCardJson = JSON.stringify(currentCard);

      const currentElementsCount = currentCard["body"]
        ? currentCard["body"].length
        : 0;
      const cardArea = document.getElementById("cardArea");

      const isOverflown =
        cardArea.querySelector("div").scrollHeight >
        cardArea.querySelector("div").clientHeight;

      if (cd.treeViewToolbox.isExpanded && isOverflown && currentElementsCount !== previousCardElementsCount) {
        cd.treeViewToolbox.collapse();
        setTimeout(() => { cd.treeViewToolbox.expand(); }, 1);
      }

      templateData.CardResponseJson = currentCardJson;
      previousCardElementsCount = currentElementsCount;
      dispatchData(templateData);
    };

    designer.onCardPayloadChanged = (cd: ACDesigner.CardDesigner) => {
      const card = cd.getBoundCard();
      const parseResult = parseInputs(card["body"]);
      const inputs = parseResult.inputIds;
      const types = parseResult.typeIds;
      const formatErrors = parseResult.formatErrors;
      removeTargetVersionWarning();
      if (newPeerCreated) {
        designer.designerSurface?.selectedPeer?.renderedElement?.click();
        newPeerCreated = false;
      } else
        updateElementProperties();

      formatErrors.forEach(err => addInputIdSpaceValidation(err));

      const containsSpaceOrStartsWithNumber = inputs?.some(idText => !idText.startsWith("Ticket.") && /[^a-zA-Z\d]|^\d/.test(idText));
      if (containsSpaceOrStartsWithNumber) addInputIdSpaceValidation(`[Validation] ${t("common.id-input-error")}`);

      const assignmentFields = [Assignment.ElementId, TeamPicker.ElementId, GroupPicker.ElementId]
      const containsAssignment = inputs?.filter(idText => assignmentFields.includes(idText)) ?? [];
      if (containsAssignment.length > 1) addInputIdSpaceValidation(`[Validation] Only a single Team/Group/Assignment field can be used as Microsoft Teams does not support dependent dropdowns.`);

      if (templateData.IsDefault) {
        const containsSubmit = inputs?.some(idText => idText == SubmitRequest.ElementId);
        if (!containsSubmit) addInputIdSpaceValidation(`[Validation] Submit Request must be present.`);
        const containsRequest = inputs?.some(idText => idText == RequestTitle.ElementId);
        if (!containsRequest) addInputIdSpaceValidation(`[Validation] Request must be present.`);
      }

      const needsUpgradeItem = types.some(elementType => elementType.startsWith("Input.") || ticketPropNeedsUpgrade(elementType));
      if (!featureSets.enableCustomForms && needsUpgradeItem) addInputIdSpaceValidation(`[Validation] ${AdaptiveCardTexts.NeedsUpgradeText}`);

      const errorPane = document.getElementById('errorPane');
      templateData.isCardValid = !errorPane.hasChildNodes();
      dispatchData(templateData);
    };

    return designer;
  };

  const removeUnwantedActions = (selector: string) => {
    const elements = document.querySelectorAll<HTMLElement>(selector);
    Array.from(elements).forEach((element, i) => {
      const textProp = "textContent" in document ? "textContent" : "innerText";
      if (
        !["Action.OpenUrl", "Action.ToggleVisibility"]?.some(
          (x) => x == element[textProp]
        )
      ) {
        element.remove();
      }
    });
  };

  const removeTargetVersionWarning = () => {
    const errorPane = document.getElementById("errorPane");
    const elements = document.querySelectorAll<HTMLElement>(
      ".acd-error-pane-message"
    );
    Array.from(elements).forEach((element, i) => {
      const textProp = "textContent" in document ? "textContent" : "innerText";
      if (element[textProp].toLowerCase().includes("target version")) {
        element.remove();
      }
    });

    errorPane.style.display = errorPane.children.length == 0 ? "none" : "block";

    const botName = document.querySelector<HTMLElement>(
      ".teams-botNameAndTime"
    );
    if (botName) {
      botName.innerText = "CARD DESIGNER";
      botName.style.color =
        currentState?.theme === themeNames.Dark ? "white" : "black";
      botName.style.fontSize = "12px";
    }
  };

  const addInputIdSpaceValidation = (message: string) => {
    const errorPane = document.getElementById('errorPane');
    if (errorPane) {
      const newDiv = document.createElement("div");
      newDiv.className = "acd-error-pane-message selectable";
      const newContent = document.createTextNode(
        message
      );
      newDiv.appendChild(newContent);

      errorPane.appendChild(newDiv);
      errorPane.style.display = 'block';

      errorPane.classList.remove("acd-hidden");
    }
  };

  const getFormatErrMessage = (element: any, formatPattern: any, errMessage: string) => {
    let formatErrors = [];
    const dataValue = element['value'] ?? "";
    const dataMax = element['max'] ?? "";
    const dataMin = element['min'] ?? "";
    if ((!formatPattern.test(dataValue) && dataValue.length > 0) ||
      (!formatPattern.test(dataMax) && dataMax.length > 0) ||
      (!formatPattern.test(dataMin) && dataMin.length > 0))
      formatErrors.push(errMessage);

    return formatErrors;
  }

  const parseCustomPropertyInputs = (container: any[]) => {
    if (container && container.length > 0) {
      const customProperties = currentState?.customProperties ?? [];
      container.forEach(element => {
        const type: string = element['type'];
        const id: string = element['id'] || "";
        const isCustomProperty: boolean = element['isCustomProperty'] || false;
        if (type == 'Container' && element['items']) {
          parseCustomPropertyInputs(element['items']);
        } else if (type == 'ColumnSet' && element['columns']) {
          element['columns'].forEach(col => {
            if (col['items']) {
              parseCustomPropertyInputs(col['items']);
            }
          });
        } else if (isCustomProperty) {
          const cp = customProperties.find(s => s.PropertyId == id);
          if (cp && cp.Name != type)
            element['type'] = cp.Name;
        }
      });
    }
  }

  const parseInputs = (container: any[]) => {
    let inputIds = [];
    let typeIds = [];
    let formatErrors = [];
    if (container && container.length > 0) {
      container.forEach(element => {
        const type: string = element['type'];
        const id: string = element['id'] || "";
        const isCustomProperty: boolean = element['isCustomProperty'] || false;
        if (type == 'Container' && element['items']) {
          const paresedInputs = parseInputs(element['items']);
          inputIds = inputIds.concat(paresedInputs.inputIds);
          typeIds = typeIds.concat(paresedInputs.typeIds);
          formatErrors = formatErrors.concat(paresedInputs.formatErrors);
        } else if (type == 'ColumnSet' && element['columns']) {
          element['columns'].forEach(col => {
            if (col['items']) {
              const paresedInputs = parseInputs(col['items']);
              inputIds = inputIds.concat(paresedInputs.inputIds);
              typeIds = typeIds.concat(paresedInputs.typeIds);
              formatErrors = formatErrors.concat(paresedInputs.formatErrors);
            }
          });
        } else if (type == 'Input.Date' && element['id']) {
          inputIds.push(id);
          typeIds.push(type);

          const validDatePattern = /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/;
          const errorMessage = "Input.Date Default/Minimum/Maximum value must have a valid format in YYYY-MM-DD.";
          formatErrors = formatErrors.concat(getFormatErrMessage(element, validDatePattern, errorMessage));

        } else if (type == 'Input.Time' && element['id']) {
          inputIds.push(id);
          typeIds.push(type);

          const twentyfourHrPattern = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;
          const timeErrMessage = "Input.Time Default/Minimum/Maximum value must have a valid format in HH:MM.";
          formatErrors = formatErrors.concat(getFormatErrMessage(element, twentyfourHrPattern, timeErrMessage));

        } else if ((type.startsWith('Input.') || type == CardPeoplePicker.JsonTypeName) && element['id']) {
          inputIds.push(element['id']);
          typeIds.push(type);
        } else if (id.startsWith('Ticket.')) {
          inputIds.push(id);
          typeIds.push(type);
        } else if (isCustomProperty) {
          inputIds.push(id);
          typeIds.push(type);

          const dataType: string = element['dataType'] || "";
          if (CustomPropertyDataType_LABELS[dataType] == CustomPropertyDataType_LABELS.BOOL) {
            const isRequired: boolean = element['requireField'] || false;
            const displayLabel: string = element['displayLabel'] || "";
            if (isRequired && displayLabel.length == 0)
              formatErrors.push("[Validation] Required inputs should have a label.");
          }
        }
      });
    }

    return { inputIds: inputIds, typeIds: typeIds, formatErrors: formatErrors };
  };

  const fixBrokenButtonImages = () => {
    const removeButtons = document.querySelectorAll<HTMLButtonElement>('.default-ac-pushButton[title="Remove"]');
    removeButtons.forEach((button) => {
      const img = button.querySelector<HTMLImageElement>("img");
      if (img && (img.src.includes("[object") || img.naturalWidth === 0)) {
        // Use a simple Unicode X character instead of a broken image
        img.style.display = "none";
        if (!button.querySelector(".tikit-remove-icon")) {
          const span = document.createElement("span");
          span.className = "tikit-remove-icon";
          span.textContent = "✕";
          span.style.fontSize = "12px";
          span.style.fontWeight = "bold";
          button.insertBefore(span, img);
        }
      }
    });
  };

  const updateElementProperties = (senderPeer?: any) => {
    const propertySheet = document.querySelector('.acd-propertySheet-host');
    if (propertySheet) {
      const allpropertyBlocks = propertySheet.querySelectorAll('.default-ac-textBlock');
      let firstTextBlockText = (allpropertyBlocks[0]?.textContent ?? "").toLowerCase().removeWhiteSpaces();
      allpropertyBlocks.forEach(el => {
        let elementText = el.textContent;
        elementText = elementText.trim().toLowerCase();
        if ((elementText === RequestTitle.JsonTypeName.toLowerCase() && firstTextBlockText === RequestTitle.JsonTypeName.toLowerCase())
          || ((elementText === AddComment.JsonTypeName.toLowerCase() && firstTextBlockText === AddComment.JsonTypeName.toLowerCase()))) {
          const parentEl = el.closest(".default-ac-columnSet");
          if (parentEl) {
            const targetContainer = parentEl.lastElementChild;
            const requestFieldMoreInfoLinkEl = propertySheet.querySelector('#requestFieldMoreInfoLink');
            if (targetContainer && !requestFieldMoreInfoLinkEl) {
              const domNode = document.createElement('div');
              const root = createRoot(domNode);
              root.render(<a id="requestFieldMoreInfoLink" href="https://get.cireson.com/adaptive-cards-more-info" target="_blank" style={{ color: "var(--swiper-theme-color)", cursor: "pointer" }}> What's this? </a>);
              targetContainer.append(domNode);
            }
          }
        } else if (elementText === "initially visible") {
          const parentEl = el.closest(".default-ac-columnSet");
          const initialVisibleFieldHint = propertySheet.querySelector('#initialVisibleFieldHint');
          if (parentEl && !initialVisibleFieldHint) {
            const domNode = document.createElement('div');
            domNode.classList.add("ml-3");
            const root = createRoot(domNode);
            root.render(<p id="initialVisibleFieldHint"> {AdaptiveCardTexts.InitiallyVisibleHint} </p>);
            parentEl.append(domNode);
          }
        } else if (elementText === "data context" || elementText === "only show when") {
          const parentEl = el.closest(".default-ac-columnSet");
          if (parentEl.nextElementSibling.classList.contains("default-ac-horizontal-separator")) {
            parentEl.nextElementSibling.remove();
            parentEl.remove();
          }
        }

        if (senderPeer) {
          const selectedPeerType = senderPeer["_cardObject"]["_propertyBag"]["type"];
          const isCustomProperty = senderPeer["_cardObject"]["_propertyBag"]["isCustomProperty"];
          if (isCustomProperty && selectedPeerType != firstTextBlockText.trim()) {
            allpropertyBlocks[0].firstElementChild.innerHTML = `<strong>${selectedPeerType}</strong>`;
          }
          setSelectedPeer(null);

          if (isCustomProperty || senderPeer.cardElement.id?.startsWith("Ticket")) {
            const firstInput = propertySheet.querySelector('.default-ac-textInput');
            firstInput?.setAttribute("disabled", "true");

            const cardObject = senderPeer["_cardObject"];
            const cpDataType = cardObject["dataType"];
            if (CustomPropertyDataType_LABELS[cpDataType] == CustomPropertyDataType_LABELS.LIST && elementText === "data source") {
              const cpPropertyId = cardObject["id"];
              const parentEl = el.closest(".default-ac-columnSet");
              const selectEl = parentEl.querySelector("select");

              const choicesData = initialPickerChoices.concat(cpDataSources
                ?.filter(c => c["CustomProperty"]?.["PropertyId"] == cpPropertyId)
                .map(x => { return { "name": x.Name, "value": x.Id.toString() } as IVersionedChoice }) ?? []);
              selectEl.options.length = 1;

              const currDataSource = cardObject["dataSource"] ?? "";
              choicesData.forEach(x => {
                let opt = document.createElement('option');
                opt.value = x.value;
                opt.selected = x.value == currDataSource;
                opt.innerHTML = `<option value="${x.value}" aria-label="${x.name}" tabindex="0">${x.name}</option>`;
                selectEl.appendChild(opt);
              });
            }
          }
        }

        fixBrokenButtonImages();
      });
      disableHiddenOrArchivedCustomProperties();
    }
  }
  const ticketPropNeedsUpgrade = (elementText: string) => {
    let isTicketPropertyKey = false;
    const availableList = [TicketPropertiesJsonTypeNames.Request, TicketPropertiesJsonTypeNames.Team, TicketPropertiesJsonTypeNames.Category, TicketPropertiesJsonTypeNames.StatusOfMyRequests, TicketPropertiesJsonTypeNames.SubmitRequest, TicketPropertiesJsonTypeNames.AddAttachment];
    for (let key of Object.keys(TicketPropertiesJsonTypeNames)) {
      if (TicketPropertiesJsonTypeNames[key] == elementText)
        isTicketPropertyKey = true;
    }
    return (isTicketPropertyKey && !availableList.some(name => name == elementText));
  }

  const upgradeUpdateElementProperties = () => {
    const toolPanel = document.querySelector('#toolPalettePanel');
    if (toolPanel) {
      const itemLabels = toolPanel.querySelectorAll(".acd-palette-item-label");
      itemLabels.forEach(label => {
        let elementText = label.textContent;
        elementText = elementText.trim();
        const customPropertyNeedsUpgrade = currentState.customProperties?.some(cp => cp.Name === elementText);

        if (elementText.startsWith("Input.") || ticketPropNeedsUpgrade(elementText) || customPropertyNeedsUpgrade) {
          label.classList.add("tikit-disabled-label");
          label.previousElementSibling.classList.add("tikit-disabled-label");
          label.parentElement.classList.add("tikit-disabled-container");
        }
      });
    }

    const parentToolPanel = document.querySelectorAll('.acd-palette-category');
    parentToolPanel?.forEach(parentPanel => {
      let elementText = parentPanel.textContent;
      elementText = elementText.trim().toLowerCase();

      if (elementText == "ticket properties" || elementText == "inputs" || "custom properties") {
        const domNode = document.createElement('div');
        domNode.title = AdaptiveCardTexts.NeedsUpgradeText;
        domNode.classList.add('tikit-acd-upgrade');
        domNode.classList.add(`tikit-acd-upgrade_themed_${currentState?.theme === themeNames.Dark ? "dark" : "light"}`);
        parentPanel.append(domNode);
        parentPanel.classList.add("tikit-category-upgrade");

      }
    });

    const cardEditorEl = document.querySelector('.monaco-editor');
    if (cardEditorEl)
      cardEditorEl.classList.add("tikit-disabled-cardeditor");
  }

  const disableHiddenOrArchivedCustomProperties = () => {
    const toolPanel = document.querySelector('#toolPalettePanel');
    if (toolPanel) {
      const itemLabels = toolPanel.querySelectorAll(".acd-palette-item-label");
      itemLabels.forEach(label => {
        let elementText = label.textContent;
        elementText = elementText.trim();
        const currentProperty = currentState.customProperties?.find(cp => cp.Name === elementText);
        if (currentProperty?.IsHidden || currentProperty?.IsArchived) {
          label.classList.add("tikit-disabled-label");
          label.previousElementSibling.classList.add("tikit-disabled-label");
          label.parentElement.classList.add(currentProperty?.IsHidden ? "tikit-disabled-container" : "tikit-archived-container");

          const customPropertyInputs = document.querySelectorAll(".tikit-customProperty");
          customPropertyInputs.forEach(cpi => {
            const cpParentElId = cpi.parentElement.id;
            if (currentProperty.PropertyId == cpParentElId) {
              const cpPrevElSibling = cpi.parentElement.previousElementSibling;
              if (cpPrevElSibling.classList.contains("ac-horizontal-separator"))
                cpPrevElSibling.remove();
              cpi.parentElement.remove();
            }
          });
        }
      });
    }
  }

  useEffect(() => {

    (async () => {
      const featureSets = await loadFeatureVars();
      setIsMDPEnabled(featureSets.enableMultiDepartment);
      const cpDSData = await initializeCustomPropertiesState(currentState, setAppState);
      setCpDataSources(cpDSData);
      if (ref.current) {
        let designer = setDesigner(ref.current, featureSets, currentState);
        setCardDesigner(designer);
        if (!featureSets.enableCustomForms)
          upgradeUpdateElementProperties();
        disableHiddenOrArchivedCustomProperties();
        initializeCustomFieldConfigurations(currentState);
        removeTargetVersionWarning();
      }
    })();

  }, [ref, reRender]);

  useEffect(() => {
    if (!open && cardDesigner) {
      if (
        templateData.CardResponseJson &&
        templateData.CardResponseJson.length > 0
      )
        cardDesigner.setCard(JSON.parse(templateData.CardResponseJson));
      else cardDesigner.setCard(blankPayloadData);
    }
  }, [open]);

  useEffect(() => {
    if (!openDataSource) {
      (async () => {
        await initializeDataSource();
        const cpDSData = await initializeCustomPropertiesState(currentState, setAppState);
        setCpDataSources(cpDSData);
      })();
    }
    else if (cardDesigner?.designerSurface?.selectedPeer?.isSelected ?? false)
      cardDesigner.designerSurface.selectedPeer.isSelected = false;
  }, [openDataSource]);

  const [isTreeviewHidden, setIsTreeviewHidden] = useState<boolean | null>(
    null
  );

  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => {
        return (
          <>
            <Dialog
              className={templateData.IsDefault ? AppCSS.quarterVWDialog : ''}
              content={
                <SetDefault_CustomForm
                  templateProps={{
                    headerContent: headerContent,
                    templateData: tempData,
                  }}
                  isUpdate
                  isMDPEnabled={isMDPEnabled}
                />
              }
              open={open}
              cancelButton="Cancel"
              confirmButton="Confirm"
              onCancel={() => {
                setOpen(false);
              }}
              onConfirm={() => {
                templateData.CardResponseJson = tempData.CardResponseJson;
                setOpen(false);
              }}
            />

            <Panel
              isLightDismiss
              isOpen={openDataSource}
              onDismiss={() => setOpenDataSource(false)}
              onOuterClick={() => setOpenDataSource(true)}
              closeButtonAriaLabel="Close"
              type={PanelType.medium}
              className={`panelFlyout`}
              style={{ width: '100%', ...ThemeColorScheme(globalTheme.siteVariables) }}
            >
              <CustomElementDataSourceMain />
            </Panel>
            <Flex column styles={{ ...ThemeColorScheme(globalTheme.siteVariables) }} className={"w-full"}>
              <div
                className={`${globalTheme.siteVariables.theme === themeNames.Dark
                    ? classNames.acdDesignerRoot
                    : classNamesLightMode.acdDesignerRoot
                  }`}
                ref={ref}
                onMouseDown={(evt) => {
                  const treeViewPanel = document.querySelector("#treeViewPanel");

                  if (
                    !treeViewPanel.classList.contains("acd-hidden") &&
                    evt.target["className"].includes("acd-peer")
                  ) {
                    treeViewPanel.classList.add("acd-hidden");
                    setIsTreeviewHidden(true);
                  }
                }}
                onMouseUp={() => {
                  const treeViewPanel = document.querySelector("#treeViewPanel");

                  if (isTreeviewHidden) {
                    setIsTreeviewHidden(false);
                    setTimeout(() => {
                      treeViewPanel.classList.remove("acd-hidden");
                    }, 50);
                  }
                }}
                onClick={(evt) => {
                  if (evt.target["className"].includes("acd-peerButton-text"))
                    removeUnwantedActions(".ms-ctrl-dropdown-item");
                  if (
                    evt.target["className"].includes(
                      "default-ac-multichoiceInput"
                    )
                  )
                    removeUnwantedActions('[value*="Action."]');

                  const element = document.querySelector<HTMLElement>(
                    '.default-ac-pushButton[title="Add an action"]'
                  );
                  if (element) {
                    element.addEventListener("click", () => {
                      removeUnwantedActions(".ms-ctrl-dropdown-item");
                    });
                  }

                  if (evt.target["className"].includes("acd-peer") && selectedPeer != null) {
                    updateElementProperties(selectedPeer);
                  } else
                    updateElementProperties();
                }}
              ></div></Flex>
          </>
        );
      }}
    />
  );
};
